import * as React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import EventRepeatIcon from '@mui/icons-material/EventRepeat'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import UndoIcon from '@mui/icons-material/Undo'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DirectionSnackbar from '../../../Components/Snackbar'
import { makeGetRequest, makePostRequest } from 'utils/api'

/**
 * LAP-769 — "Générer planning".
 *
 * Bouton du header du calendrier : lit les contraintes de vie de l'utilisateur
 * (travail, sport, chien, famille…) et propose un planning de la semaine, sans
 * chevauchement avec les événements existants. Même trame preview/validation/
 * commit que GE022 (catégorisation Finance) : on PROPOSE sans écrire, l'utilisateur
 * coche/décoche les blocs, puis on APPLIQUE de façon atomique.
 *
 * Moteur règle-based côté backend : gratuit, aucun crédit consommé.
 *
 * Props :
 *  - onComplete : callback après application réussie (refresh du calendrier).
 * Expose handleClickOpen() via ref (même contrat que AutoCategorizeModal).
 */

// Pastilles de domaine, cohérentes avec la lecture rapide du planning.
const DOMAIN_COLORS = {
  work: '#1b75d0',
  routine: '#d39a3a',
  sport: '#2e9e6b',
  dog: '#c98a2b',
  family: '#9b59b6',
  meal: '#e0734a',
  sleep: '#5a6b7b',
  social: '#3aa6a0',
  task: '#6d7fd6',
  chore: '#8a7d68',
  errand: '#b1684a',
  goal: '#3f7e4e',
  personal: '#c0567f',
  other: '#7c8a99',
}

// Lundi de la semaine d'une date (YYYY-MM-DD).
function mondayOf(d) {
  const date = new Date(d)
  const day = (date.getDay() + 6) % 7 // 0 = lundi
  date.setDate(date.getDate() - day)
  return date
}

function toISODate(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

// Identifiant de génération (≤ 32 car.) envoyé sur chaque tranche d'apply afin
// que tous les événements d'un même planning partagent un "batch" annulable.
function makeBatchId() {
  const rnd = Math.random().toString(36).slice(2, 8)
  return `gen-${Date.now().toString(36)}-${rnd}`.slice(0, 32)
}

// "90" -> "1 h 30", "120" -> "2 h", "45" -> "45 min".
function fmtDuration(min) {
  const m = Number(min) || 0
  const h = Math.floor(m / 60)
  const r = m % 60
  if (h && r) return `${h} h ${r}`
  if (h) return `${h} h`
  return `${r} min`
}

const GeneratePlanningModal = React.forwardRef((props, ref) => {
  const { onComplete } = props
  const { t } = useTranslation('layout')
  const tr = (k, dflt, opts) =>
    t(`time.planning.${k}`, { defaultValue: dflt, ...(opts || {}) })

  const [open, setOpen] = React.useState(false)
  const [step, setStep] = React.useState('config') // 'config' | 'review' | 'done'
  const [weekStart, setWeekStart] = React.useState(() => toISODate(mondayOf(new Date())))
  // LAP-771 — horizon de génération : "week" (historique), "month" ou "quarter".
  const [horizon, setHorizon] = React.useState('week')
  const [loading, setLoading] = React.useState(false)
  const [applying, setApplying] = React.useState(false)
  const [applyProgress, setApplyProgress] = React.useState(null)
  const [preview, setPreview] = React.useState(null)
  const [rows, setRows] = React.useState([])
  const [snack, setSnack] = React.useState({ open: false, color: 'info', message: '' })

  // — Annulation groupée (LAP-769) — batch du dernier planning appliqué —
  const [appliedBatch, setAppliedBatch] = React.useState(null)
  const [appliedCount, setAppliedCount] = React.useState(0)
  const [cancelling, setCancelling] = React.useState(false)
  // Liste des générations récentes (annulables après un rechargement).
  const [runs, setRuns] = React.useState([])
  const [loadingRuns, setLoadingRuns] = React.useState(false)
  const [cancellingBatch, setCancellingBatch] = React.useState(null)

  // — Questionnaire de génération —
  const [context, setContext] = React.useState(null) // {has_week_menu, has_shopping_list}
  const [hasEngagements, setHasEngagements] = React.useState(false)
  const [engagements, setEngagements] = React.useState([])
  // Catégories du calendrier, pour rattacher une obligation à la bonne catégorie.
  const [categories, setCategories] = React.useState([])
  // Sports choisis dans l'écran de sélection (plus d'injection par défaut).
  const [sportPlans, setSportPlans] = React.useState([])
  // Habitudes MesToDo proposées cochées (id -> bool).
  const [habits, setHabits] = React.useState([])
  const [habitChecks, setHabitChecks] = React.useState({})
  const [shortages, setShortages] = React.useState('')
  const [options, setOptions] = React.useState({
    plan_meals: false,
    plan_groceries: false,
    plan_laundry: true,
    cleaning_daily: true,
    cleaning_weekly: true,
    cleaning_monthly: false,
  })

  const resetFlow = () => {
    setStep('config')
    setPreview(null)
    setRows([])
    setApplyProgress(null)
    setAppliedBatch(null)
    setAppliedCount(0)
  }

  const handleClickOpen = () => {
    resetFlow()
    setOpen(true)
  }
  const handleClose = () => {
    if (loading || applying) return
    setOpen(false)
    resetFlow()
  }

  // Ouvre directement l'écran « Annuler une génération » (depuis le calendrier).
  const fetchRuns = async () => {
    setLoadingRuns(true)
    const res = await makeGetRequest('/api/time/planning/runs/', {}, true)
    setLoadingRuns(false)
    if (res && res.status === 200) {
      setRuns((res.data && res.data.runs) || [])
    } else {
      showSnack('error', tr('errorGeneric', 'Une erreur est survenue.'))
    }
  }
  const openRuns = () => {
    resetFlow()
    setStep('runs')
    setOpen(true)
    fetchRuns()
  }

  React.useImperativeHandle(ref, () => ({ handleClickOpen, openRuns }))

  const showSnack = (color, message) => {
    setSnack({ open: true, color, message })
    setTimeout(() => setSnack({ open: false, color, message: '' }), 3500)
  }

  const setOption = (patch) => setOptions((prev) => ({ ...prev, ...patch }))

  // Charge le contexte de la semaine (menu / liste de courses) pour pré-cocher
  // les bonnes questions : pas de menu → proposer de préparer les menus ;
  // menu mais pas de liste → proposer de faire les courses.
  React.useEffect(() => {
    if (!open || !weekStart) return
    let cancelled = false
    const run = async () => {
      const res = await makePostRequest(
        '/api/time/planning/context/',
        { week_start: weekStart },
        {},
        true,
      )
      if (cancelled) return
      if (res && res.status === 200) {
        const ctx = res.data || {}
        setContext(ctx)
        setOptions((prev) => ({
          ...prev,
          plan_meals: !ctx.has_week_menu,
          plan_groceries: !!ctx.has_week_menu && !ctx.has_shopping_list,
        }))
        // Sports : listés mais NON cochés par défaut (l'utilisateur choisit
        // lesquels planifier, en fixe ou flexible, avec date/heure/durée).
        setSportPlans(
          (ctx.sports || []).map((s) => ({
            id: s.id,
            name: s.name,
            included: false,
            mode: 'flexible',
            date: weekStart,
            start_time: '18:00',
            duration_min: 60,
            sessions: 1,
            window_start: '18:00',
            window_end: '21:00',
          })),
        )
        // Habitudes : toutes proposées cochées, l'utilisateur peut décocher.
        const list = ctx.habits || []
        setHabits(list)
        setHabitChecks(
          list.reduce((acc, h) => {
            acc[h.id] = true
            return acc
          }, {}),
        )
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [open, weekStart])

  // Catégories du calendrier (pour rattacher une obligation) — chargées une fois
  // à l'ouverture, indépendamment de la semaine.
  React.useEffect(() => {
    if (!open || categories.length > 0) return
    let cancelled = false
    const run = async () => {
      const res = await makeGetRequest('/api/time/get-category-dd', {}, true)
      if (cancelled) return
      if (res && res.status === 200) {
        const raw = res.data
        const list = Array.isArray(raw) ? raw : Array.isArray(raw?.results) ? raw.results : []
        // Dédoublonnage par nom (les catégories sont dupliquées par langue).
        const seen = new Set()
        const uniq = []
        for (const c of list) {
          if (c && c.name && !seen.has(c.name)) {
            seen.add(c.name)
            uniq.push({ id: c.id, name: c.name })
          }
        }
        setCategories(uniq)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [open])

  const addEngagement = () =>
    setEngagements((prev) => [
      ...prev,
      { label: '', date: weekStart, start_time: '18:00', duration_min: 60, category_id: '' },
    ])
  const updateEngagement = (idx, patch) =>
    setEngagements((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)))
  const removeEngagement = (idx) =>
    setEngagements((prev) => prev.filter((_, i) => i !== idx))

  const updateSport = (id, patch) =>
    setSportPlans((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)))
  const toggleHabit = (id, checked) =>
    setHabitChecks((prev) => ({ ...prev, [id]: checked }))

  // Transforme les sports cochés en payload attendu par _sport_requests.
  const buildSportsPayload = () =>
    sportPlans
      .filter((s) => s.included && (Number(s.duration_min) || 0) > 0)
      .map((s) =>
        s.mode === 'fixed'
          ? {
              mode: 'fixed',
              name: s.name,
              date: s.date,
              start_time: s.start_time,
              duration_min: Number(s.duration_min) || 60,
            }
          : {
              mode: 'flexible',
              name: s.name,
              sessions: Number(s.sessions) || 1,
              duration_min: Number(s.duration_min) || 60,
              window_start: s.window_start,
              window_end: s.window_end,
            },
      )

  const domainLabel = (domain) =>
    tr(`domain.${domain}`, domain.charAt(0).toUpperCase() + domain.slice(1))

  const dayLabel = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
      })
    } catch (_) {
      return iso
    }
  }

  // Étape 1 : génère l'aperçu (aucune écriture).
  const handleGenerate = async () => {
    const errGeneric = tr('errorGeneric', 'Une erreur est survenue.')
    setLoading(true)
    const cleanEngagements = hasEngagements
      ? engagements
          .filter((e) => e.label.trim() && e.date && e.start_time)
          .map((e) => ({
            label: e.label.trim(),
            date: e.date,
            start_time: e.start_time,
            duration_min: Number(e.duration_min) || 60,
            category_id: e.category_id ? Number(e.category_id) : null,
          }))
      : []
    const note = shortages.trim()
    const payloadOptions = {
      ...options,
      // Si l'utilisateur signale un manque, on force la planification des courses.
      plan_groceries: options.plan_groceries || !!note,
      shortages: note,
    }
    const sports = buildSportsPayload()
    const habitIds = habits
      .filter((h) => habitChecks[h.id])
      .map((h) => h.id)
    const response = await makePostRequest(
      '/api/time/planning/preview/',
      {
        week_start: weekStart,
        horizon,
        engagements: cleanEngagements,
        options: payloadOptions,
        sports,
        habit_ids: habitIds,
      },
      {},
      true,
    )
    setLoading(false)
    if (!response || response.status !== 200) {
      showSnack('error', (response && response.data && response.data.detail) || errGeneric)
      return
    }
    const data = response.data || {}
    setPreview(data)
    // Les propositions sont cochées par défaut ; les suggestions (créneaux libres
    // proposés pour couvrir les blocs non placés) sont listées décochées (opt-in).
    const proposalRows = (data.proposals || []).map((p) => ({ ...p, include: true }))
    const suggestionRows = (data.suggestions || []).map((s) => ({
      ...s,
      suggestion: true,
      include: false,
    }))
    setRows([...proposalRows, ...suggestionRows])
    if (proposalRows.length === 0 && suggestionRows.length === 0) {
      showSnack(
        'info',
        tr('noProposals', 'Aucun bloc à proposer. Ajoutez des contraintes (sport, chien, famille…).'),
      )
      return
    }
    setStep('review')
  }

  const includedCount = rows.filter((r) => r.include).length

  const toggleAll = (checked) =>
    setRows((prev) => prev.map((r) => ({ ...r, include: checked })))

  const setRow = (id, patch) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)))

  // Étape 2 : applique les blocs cochés, par tranches de 400 (commit atomique back).
  const handleApply = async () => {
    const errGeneric = tr('errorGeneric', 'Une erreur est survenue.')
    const events = rows
      .filter((r) => r.include)
      .map((r) => ({
        date: r.date,
        start_time: r.start_time,
        end_time: r.end_time,
        domain: r.domain,
        label: r.label,
        category_id: r.category_id || null,
        // LAP-771 — remonte la contrainte d'origine pour actualiser sa périodicité.
        constraint_id: r.constraint_id || null,
      }))
    if (events.length === 0) return

    setApplying(true)
    const SLICE = 400
    // Même batch sur toutes les tranches → annulation groupée possible.
    const batch = makeBatchId()
    let createdAcc = 0
    let processedAcc = 0
    let failed = false
    setApplyProgress({ processed: 0, total: events.length })

    for (let i = 0; i < events.length; i += SLICE) {
      const slice = events.slice(i, i + SLICE)
      const response = await makePostRequest(
        '/api/time/planning/apply/',
        { events: slice, batch, week_start: weekStart },
        {},
        true,
      )
      if (!response || response.status !== 200) {
        showSnack('error', (response && response.data && response.data.detail) || errGeneric)
        failed = true
        break
      }
      createdAcc += (response.data && response.data.created) || 0
      processedAcc += slice.length
      setApplyProgress({ processed: processedAcc, total: events.length })
    }

    setApplying(false)
    if (createdAcc > 0 && typeof onComplete === 'function') {
      onComplete()
    }
    // Jamais de faux succès : si le back n'a RIEN créé (toutes les lignes
    // ignorées), l'écran vert « Planning ajouté au calendrier » mentirait —
    // c'est le bug « il dit que c'est bien généré mais rien d'affiché ».
    // On reste sur l'étape de revue et on affiche le detail du back.
    if (!failed && createdAcc === 0) {
      showSnack(
        'error',
        tr(
          'applyNothingCreated',
          "Aucun événement n'a été créé — le planning n'a pas été ajouté au calendrier.",
        ),
      )
      return
    }
    if (!failed) {
      // On garde le modal ouvert sur un écran de confirmation offrant l'annulation
      // groupée du planning qui vient d'être créé.
      setAppliedBatch(batch)
      setAppliedCount(createdAcc)
      setStep('done')
    }
  }

  // Annule en un coup tout le planning qui vient d'être généré (LAP-769).
  const handleCancelGeneration = async () => {
    if (!appliedBatch) return
    const errGeneric = tr('errorGeneric', 'Une erreur est survenue.')
    setCancelling(true)
    const response = await makePostRequest(
      '/api/time/planning/cancel/',
      { batch: appliedBatch },
      {},
      true,
    )
    setCancelling(false)
    if (!response || response.status !== 200) {
      showSnack('error', (response && response.data && response.data.detail) || errGeneric)
      return
    }
    const deleted = (response.data && response.data.deleted) || 0
    if (typeof onComplete === 'function') onComplete()
    showSnack(
      'success',
      tr('cancelled', '{{count}} événement(s) supprimé(s). Planning annulé.', { count: deleted }),
    )
    setTimeout(() => {
      setOpen(false)
      resetFlow()
    }, 1000)
  }

  // Annule une génération précise depuis la liste des runs (après rechargement).
  const handleCancelRun = async (batch) => {
    const errGeneric = tr('errorGeneric', 'Une erreur est survenue.')
    setCancellingBatch(batch)
    const response = await makePostRequest(
      '/api/time/planning/cancel/',
      { batch },
      {},
      true,
    )
    setCancellingBatch(null)
    if (!response || response.status !== 200) {
      showSnack('error', (response && response.data && response.data.detail) || errGeneric)
      return
    }
    const deleted = (response.data && response.data.deleted) || 0
    setRuns((prev) => prev.filter((r) => r.batch !== batch))
    if (typeof onComplete === 'function') onComplete()
    showSnack(
      'success',
      tr('cancelled', '{{count}} événement(s) supprimé(s). Planning annulé.', { count: deleted }),
    )
  }

  const runDateLabel = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    } catch (_) {
      return iso
    }
  }

  const conflictCount = rows.filter((r) => r.conflict).length
  const suggestionCount = rows.filter((r) => r.suggestion).length

  return (
    <>
      {snack.open && <DirectionSnackbar message={snack.message} color={snack.color} />}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth={step === 'review' ? 'lg' : 'sm'}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {step === 'runs' ? <UndoIcon fontSize="small" /> : <EventRepeatIcon fontSize="small" />}
          {step === 'runs'
            ? tr('runsTitle', 'Annuler une génération')
            : tr('title', 'Générer le planning de la semaine')}
        </DialogTitle>
        <DialogContent dividers>
          {step === 'runs' ? (
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                {tr(
                  'runsHint',
                  'Annulez un planning généré en un clic : tous ses événements sont retirés du calendrier d’un coup.',
                )}
              </Typography>
              {loadingRuns ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={26} />
                </Box>
              ) : runs.length === 0 ? (
                <Alert severity="info">
                  {tr('runsEmpty', 'Aucune génération à annuler pour le moment.')}
                </Alert>
              ) : (
                <TableContainer
                  sx={{ maxHeight: 420, border: 1, borderColor: 'divider', borderRadius: 1 }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{tr('runWeek', 'Semaine')}</TableCell>
                        <TableCell>{tr('runCreated', 'Générée le')}</TableCell>
                        <TableCell align="center">{tr('runCount', 'Événements')}</TableCell>
                        <TableCell align="right">{tr('colActions', 'Actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {runs.map((r) => (
                        <TableRow key={r.batch} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {r.week_start ? runDateLabel(r.week_start) : '—'}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {r.created_at ? runDateLabel(r.created_at) : '—'}
                          </TableCell>
                          <TableCell align="center">{r.event_count}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <Button
                              size="small"
                              color="error"
                              startIcon={
                                cancellingBatch === r.batch ? (
                                  <CircularProgress size={14} color="inherit" />
                                ) : (
                                  <UndoIcon />
                                )
                              }
                              disabled={cancellingBatch === r.batch}
                              onClick={() => handleCancelRun(r.batch)}
                            >
                              {tr('cancelRun', 'Annuler')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          ) : step === 'done' ? (
            <Stack spacing={2} sx={{ py: 2, alignItems: 'center', textAlign: 'center' }}>
              <CheckCircleOutlineIcon color="success" sx={{ fontSize: 48 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {tr('doneTitle', 'Planning ajouté au calendrier')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tr('doneSubtitle', '{{count}} événement(s) créé(s) pour la semaine.', {
                  count: appliedCount,
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {tr(
                  'doneUndoHint',
                  'Vous pouvez annuler tout ce planning d’un coup, sans supprimer chaque tâche.',
                )}
              </Typography>
            </Stack>
          ) : step === 'review' ? (
            applying ? (
              <Stack spacing={2} sx={{ py: 3 }}>
                <Typography variant="body2">
                  {tr('applying', 'Application en cours… {{processed}} / {{total}}', {
                    processed: applyProgress ? applyProgress.processed : 0,
                    total: applyProgress ? applyProgress.total : includedCount,
                  })}
                </Typography>
                <LinearProgress
                  variant={
                    applyProgress && applyProgress.total > 0 ? 'determinate' : 'indeterminate'
                  }
                  value={
                    applyProgress && applyProgress.total > 0
                      ? Math.min(100, Math.round((applyProgress.processed / applyProgress.total) * 100))
                      : undefined
                  }
                />
              </Stack>
            ) : (
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {tr('reviewTitle', 'Vérifiez les blocs proposés')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {tr(
                      'reviewSubtitle',
                      'Décochez les blocs que vous ne voulez pas. Les conflits avec un événement existant sont surlignés.',
                    )}
                  </Typography>
                </Box>

                {(preview?.unplaced?.length || 0) > 0 && (
                  <Alert severity="info" sx={{ py: 0 }}>
                    {tr(
                      'unplaced',
                      "{{count}} bloc(s) n'ont pas pu être placés (semaine trop chargée).",
                      { count: preview.unplaced.length },
                    )}
                  </Alert>
                )}
                {conflictCount > 0 && (
                  <Alert severity="warning" sx={{ py: 0 }}>
                    {tr(
                      'conflicts',
                      '{{count}} bloc(s) se superposent à un événement existant. Ils sont quand même créés — décochez-les si vous préférez les écarter.',
                      { count: conflictCount },
                    )}
                  </Alert>
                )}

                {(preview?.free_slots?.length || 0) > 0 && (
                  <Alert
                    severity="success"
                    icon={<LightbulbOutlinedIcon fontSize="inherit" />}
                    sx={{ py: 0 }}
                  >
                    {tr(
                      'freeTime',
                      '{{count}} espace(s) de temps libre cette semaine ({{total}} au total).',
                      {
                        count: preview.free_slots.length,
                        total: fmtDuration(
                          preview.free_slots.reduce((acc, f) => acc + (f.minutes || 0), 0),
                        ),
                      },
                    )}
                    {suggestionCount > 0 &&
                      ' ' +
                        tr(
                          'suggestionsHint',
                          '{{n}} suggestion(s) (surlignée(s)) pour couvrir les blocs non placés — cochez-les pour les ajouter.',
                          { n: suggestionCount },
                        )}
                  </Alert>
                )}

                <TableContainer
                  sx={{ maxHeight: 440, border: 1, borderColor: 'divider', borderRadius: 1 }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Tooltip title={tr('includeAll', 'Tout cocher / décocher')}>
                            <Checkbox
                              size="small"
                              checked={rows.length > 0 && rows.every((r) => r.include)}
                              indeterminate={
                                rows.some((r) => r.include) && !rows.every((r) => r.include)
                              }
                              onChange={(e) => toggleAll(e.target.checked)}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>{tr('colDomain', 'Domaine')}</TableCell>
                        <TableCell>{tr('colLabel', 'Bloc')}</TableCell>
                        <TableCell>{tr('colDay', 'Jour')}</TableCell>
                        <TableCell align="center">{tr('colTime', 'Horaire')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((r) => (
                        <TableRow
                          key={r.id}
                          hover
                          sx={
                            r.conflict
                              ? { bgcolor: 'rgba(251,191,36,0.14)' }
                              : r.suggestion
                                ? { bgcolor: 'rgba(46,158,107,0.10)' }
                                : undefined
                          }
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={r.include}
                              onChange={(e) => setRow(r.id, { include: e.target.checked })}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={domainLabel(r.domain)}
                              sx={{
                                bgcolor: DOMAIN_COLORS[r.domain] || DOMAIN_COLORS.other,
                                color: '#fff',
                                fontWeight: 700,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Typography variant="body2" noWrap title={r.label}>
                                {r.label}
                              </Typography>
                              {r.suggestion && (
                                <Chip
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  icon={<LightbulbOutlinedIcon />}
                                  label={tr('suggestionTag', 'suggestion')}
                                  sx={{ height: 20, fontSize: 10 }}
                                />
                              )}
                            </Stack>
                            {r.conflict && (
                              <Typography variant="caption" color="warning.main">
                                {tr('conflictTag', 'conflit')}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                            {dayLabel(r.date)}
                          </TableCell>
                          <TableCell align="center" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                            {r.start_time}–{r.end_time}
                          </TableCell>
                        </TableRow>
                      ))}
                      {rows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                            {tr('noRows', 'Aucun bloc à afficher.')}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            )
          ) : (
            <Stack spacing={2.2}>
              <Typography variant="body2" color="text.secondary">
                {tr(
                  'configHint',
                  'Le planning est construit à partir de vos contraintes (travail, sport, chien, famille) et placé sans chevaucher vos événements existants.',
                )}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  type="date"
                  size="small"
                  fullWidth
                  label={tr('weekStart', 'À partir du')}
                  InputLabelProps={{ shrink: true }}
                  value={weekStart}
                  onChange={(e) => setWeekStart(e.target.value)}
                  helperText={tr('weekStartHint', 'La semaine commence le lundi.')}
                />
                <TextField
                  select
                  size="small"
                  fullWidth
                  label={tr('horizon', 'Durée à générer')}
                  value={horizon}
                  onChange={(e) => setHorizon(e.target.value)}
                  helperText={tr(
                    'horizonHint',
                    'Semaine, mois ou trimestre. Les contraintes mensuelles / trimestrielles / annuelles sont espacées selon leur périodicité.',
                  )}
                >
                  <MenuItem value="week">{tr('horizonWeek', 'La semaine')}</MenuItem>
                  <MenuItem value="month">{tr('horizonMonth', 'Le mois')}</MenuItem>
                  <MenuItem value="quarter">{tr('horizonQuarter', 'Le trimestre')}</MenuItem>
                </TextField>
              </Stack>

              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">
                  {tr('sectionEngagements', 'Engagements importants')}
                </Typography>
              </Divider>
              <FormControlLabel
                control={
                  <Switch
                    checked={hasEngagements}
                    onChange={(e) => {
                      setHasEngagements(e.target.checked)
                      if (e.target.checked && engagements.length === 0) addEngagement()
                    }}
                  />
                }
                label={tr('engQuestion', 'Avez-vous des engagements importants cette semaine ?')}
              />
              {hasEngagements && (
                <Stack spacing={1}>
                  {engagements.map((e, idx) => (
                    <Stack
                      key={idx}
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                    >
                      <TextField
                        size="small"
                        sx={{ flex: 2 }}
                        placeholder={tr('engLabel', 'Quoi ? (ex. RDV médecin)')}
                        value={e.label}
                        onChange={(ev) => updateEngagement(idx, { label: ev.target.value })}
                      />
                      <TextField
                        size="small"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={e.date}
                        onChange={(ev) => updateEngagement(idx, { date: ev.target.value })}
                      />
                      <TextField
                        size="small"
                        type="time"
                        InputLabelProps={{ shrink: true }}
                        value={e.start_time}
                        onChange={(ev) => updateEngagement(idx, { start_time: ev.target.value })}
                      />
                      <TextField
                        size="small"
                        type="number"
                        sx={{ width: 96 }}
                        label={tr('engDuration', 'min')}
                        InputLabelProps={{ shrink: true }}
                        value={e.duration_min}
                        onChange={(ev) => updateEngagement(idx, { duration_min: ev.target.value })}
                      />
                      <TextField
                        select
                        size="small"
                        sx={{ minWidth: 150 }}
                        label={tr('engCategory', 'Catégorie')}
                        InputLabelProps={{ shrink: true }}
                        value={e.category_id ?? ''}
                        onChange={(ev) => updateEngagement(idx, { category_id: ev.target.value })}
                      >
                        <MenuItem value="">
                          <em>{tr('engCategoryNone', 'Automatique')}</em>
                        </MenuItem>
                        {categories.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </TextField>
                      <IconButton size="small" onClick={() => removeEngagement(idx)}>
                        <DeleteOutlineIcon fontSize="inherit" />
                      </IconButton>
                    </Stack>
                  ))}
                  <Button size="small" startIcon={<AddIcon />} onClick={addEngagement} sx={{ alignSelf: 'flex-start' }}>
                    {tr('engAdd', 'Ajouter un engagement')}
                  </Button>
                </Stack>
              )}

              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">
                  <FitnessCenterIcon
                    fontSize="inherit"
                    sx={{ verticalAlign: 'middle', mr: 0.5 }}
                  />
                  {tr('sectionSports', 'Mes sports')}
                </Typography>
              </Divider>
              {sportPlans.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  {tr(
                    'noSports',
                    'Aucun sport enregistré. Ajoutez-en dans Santé pour les planifier ici.',
                  )}
                </Typography>
              ) : (
                <Stack spacing={1}>
                  <Typography variant="caption" color="text.secondary">
                    {tr(
                      'sportsHint',
                      'Cochez les sports à planifier puis choisissez un créneau fixe (jour/heure précis) ou flexible (placé automatiquement dans une fenêtre).',
                    )}
                  </Typography>
                  {sportPlans.map((s) => (
                    <Box
                      key={s.id}
                      sx={{
                        border: 1,
                        borderColor: s.included ? 'primary.light' : 'divider',
                        borderRadius: 1,
                        p: 1,
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        flexWrap="wrap"
                        gap={1}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={s.included}
                              onChange={(e) => updateSport(s.id, { included: e.target.checked })}
                            />
                          }
                          label={s.name}
                        />
                        {s.included && (
                          <TextField
                            select
                            size="small"
                            sx={{ minWidth: 150 }}
                            label={tr('sportMode', 'Type de créneau')}
                            value={s.mode}
                            onChange={(e) => updateSport(s.id, { mode: e.target.value })}
                          >
                            <MenuItem value="fixed">{tr('sportFixed', 'Fixe (jour/heure)')}</MenuItem>
                            <MenuItem value="flexible">{tr('sportFlexible', 'Flexible (auto)')}</MenuItem>
                          </TextField>
                        )}
                      </Stack>
                      {s.included && s.mode === 'fixed' && (
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          sx={{ mt: 1 }}
                        >
                          <TextField
                            size="small"
                            type="date"
                            label={tr('sportDate', 'Jour')}
                            InputLabelProps={{ shrink: true }}
                            value={s.date}
                            onChange={(e) => updateSport(s.id, { date: e.target.value })}
                          />
                          <TextField
                            size="small"
                            type="time"
                            label={tr('sportStart', 'Heure')}
                            InputLabelProps={{ shrink: true }}
                            value={s.start_time}
                            onChange={(e) => updateSport(s.id, { start_time: e.target.value })}
                          />
                          <TextField
                            size="small"
                            type="number"
                            sx={{ width: 130 }}
                            label={tr('sportDuration', 'Durée (min)')}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: 0, step: 5 }}
                            value={s.duration_min}
                            onChange={(e) => updateSport(s.id, { duration_min: e.target.value })}
                          />
                        </Stack>
                      )}
                      {s.included && s.mode === 'flexible' && (
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          sx={{ mt: 1 }}
                        >
                          <TextField
                            size="small"
                            type="number"
                            sx={{ width: 110 }}
                            label={tr('sportSessions', 'Séances')}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: 1, step: 1 }}
                            value={s.sessions}
                            onChange={(e) => updateSport(s.id, { sessions: e.target.value })}
                          />
                          <TextField
                            size="small"
                            type="number"
                            sx={{ width: 130 }}
                            label={tr('sportDuration', 'Durée (min)')}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: 0, step: 5 }}
                            value={s.duration_min}
                            onChange={(e) => updateSport(s.id, { duration_min: e.target.value })}
                          />
                          <TextField
                            size="small"
                            type="time"
                            label={tr('sportWinStart', 'Fenêtre — de')}
                            InputLabelProps={{ shrink: true }}
                            value={s.window_start}
                            onChange={(e) => updateSport(s.id, { window_start: e.target.value })}
                          />
                          <TextField
                            size="small"
                            type="time"
                            label={tr('sportWinEnd', 'Fenêtre — à')}
                            InputLabelProps={{ shrink: true }}
                            value={s.window_end}
                            onChange={(e) => updateSport(s.id, { window_end: e.target.value })}
                          />
                        </Stack>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}

              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">
                  <AutorenewIcon
                    fontSize="inherit"
                    sx={{ verticalAlign: 'middle', mr: 0.5 }}
                  />
                  {tr('sectionHabits', 'Mes habitudes')}
                </Typography>
              </Divider>
              {habits.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  {tr(
                    'noHabits',
                    "Aucune habitude active dans MesToDo. Celles que vous créez seront proposées ici.",
                  )}
                </Typography>
              ) : (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {tr(
                      'habitsHint',
                      'Vos habitudes sont intégrées au planning selon leur fréquence. Décochez celles à ignorer cette semaine.',
                    )}
                  </Typography>
                  <Stack sx={{ mt: 0.5 }}>
                    {habits.map((h) => (
                      <FormControlLabel
                        key={h.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={!!habitChecks[h.id]}
                            onChange={(e) => toggleHabit(h.id, e.target.checked)}
                          />
                        }
                        label={
                          <span>
                            {h.name}{' '}
                            <Chip
                              size="small"
                              variant="outlined"
                              label={tr(`recurrence.${h.recurrence}`, h.recurrence)}
                              sx={{ height: 18, fontSize: 10 }}
                            />
                          </span>
                        }
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">
                  {tr('sectionHome', 'Intendance de la semaine')}
                </Typography>
              </Divider>

              {context && !context.has_week_menu && (
                <Alert severity="info" sx={{ py: 0 }}>
                  {tr('noMenu', 'Aucun menu détecté pour cette semaine — planifions sa préparation.')}
                </Alert>
              )}
              {context && context.has_week_menu && !context.has_shopping_list && (
                <Alert severity="info" sx={{ py: 0 }}>
                  {tr('menuNoList', "Menu présent mais pas de liste de courses — planifions les courses.")}
                </Alert>
              )}

              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={options.plan_meals}
                      onChange={(e) => setOption({ plan_meals: e.target.checked })}
                    />
                  }
                  label={tr('optMeals', 'Préparer les menus de la semaine')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={options.plan_groceries}
                      onChange={(e) => setOption({ plan_groceries: e.target.checked })}
                    />
                  }
                  label={tr('optGroceries', 'Faire les courses')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={options.plan_laundry}
                      onChange={(e) => setOption({ plan_laundry: e.target.checked })}
                    />
                  }
                  label={tr('optLaundry', 'Faire la lessive')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={options.cleaning_daily}
                      onChange={(e) => setOption({ cleaning_daily: e.target.checked })}
                    />
                  }
                  label={tr('optCleanDaily', 'Ménage quotidien')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={options.cleaning_weekly}
                      onChange={(e) => setOption({ cleaning_weekly: e.target.checked })}
                    />
                  }
                  label={tr('optCleanWeekly', 'Ménage hebdomadaire')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={options.cleaning_monthly}
                      onChange={(e) => setOption({ cleaning_monthly: e.target.checked })}
                    />
                  }
                  label={tr('optCleanMonthly', 'Ménage mensuel')}
                />
              </Box>

              <TextField
                size="small"
                fullWidth
                label={tr('shortages', 'Quelque chose risque de manquer ? (optionnel)')}
                placeholder={tr('shortagesPh', 'ex. lessive, café, croquettes du chien')}
                value={shortages}
                onChange={(e) => setShortages(e.target.value)}
                helperText={tr('shortagesHint', 'Ajouté à un créneau « Faire les courses ».')}
              />

              <Alert severity="info" sx={{ py: 0 }}>
                {tr('free', 'Génération gratuite — aucun crédit consommé.')}
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {step === 'runs' ? (
            <Button
              onClick={() => {
                setOpen(false)
                resetFlow()
              }}
            >
              {tr('close', 'Fermer')}
            </Button>
          ) : step === 'done' ? (
            <>
              <Button
                color="error"
                startIcon={cancelling ? <CircularProgress size={16} color="inherit" /> : <UndoIcon />}
                onClick={handleCancelGeneration}
                disabled={cancelling}
              >
                {tr('cancelGeneration', 'Annuler ce planning')}
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="contained"
                onClick={() => {
                  setOpen(false)
                  resetFlow()
                }}
                disabled={cancelling}
              >
                {tr('done', 'Terminé')}
              </Button>
            </>
          ) : step === 'review' ? (
            <>
              <Button onClick={() => resetFlow()} disabled={applying}>
                {tr('back', 'Retour')}
              </Button>
              <Box sx={{ flex: 1 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                {tr('willApply', '{{count}} bloc(s) seront ajoutés', { count: includedCount })}
              </Typography>
              <Button
                variant="contained"
                onClick={handleApply}
                disabled={applying || includedCount === 0}
                startIcon={applying ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {tr('apply', 'Ajouter au calendrier')}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleClose} disabled={loading}>
                {tr('cancel', 'Annuler')}
              </Button>
              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <EventRepeatIcon />}
              >
                {tr('generate', 'Générer')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
})

GeneratePlanningModal.displayName = 'GeneratePlanningModal'

export default GeneratePlanningModal
